import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

export default function About() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于成全影院',
    description: '成全影院是领先的免费在线影视平台，为您提供海量高清电影、电视剧、综艺、动漫，无需付费，即点即看。',
    url: 'https://chengquan.tv/about',
    about: {
      '@type': 'Organization',
      name: '成全影院',
      url: 'https://chengquan.tv/',
      description: '成全影院 - 免费高清在线影视平台',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'service@chengquan.tv',
      },
    },
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="成全影院是领先的免费在线影视平台，为您提供海量高清电影、电视剧、综艺、动漫，无需付费，即点即看。了解成全影院的品牌故事、用户口碑和联系方式。"
        keywords="成全影院,关于我们,品牌介绍,用户口碑,联系方式,免费电影"
        canonicalUrl="https://chengquan.tv/about"
        schema={aboutSchema}
      />

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">关于我们</h1>
              <p className="text-sm text-foreground-400">了解成全影院的故事与使命</p>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-1 bg-background-900 rounded-full p-1 mb-10 w-fit">
              {[
                { id: 'intro' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
                { id: 'reputation' as const, label: '用户口碑', icon: 'ri-heart-line' },
                { id: 'contact' as const, label: '联系方式', icon: 'ri-mail-line' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tab.icon} text-sm`}></i>
                  </div>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Brand Introduction */}
            {activeTab === 'intro' && (
              <section aria-label="品牌介绍" className="space-y-10">
                <div className="bg-background-900/50 rounded-2xl border border-background-800/50 p-6 md:p-10">
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-primary-500/10 flex items-center justify-center">
                        <i className="ri-movie-2-line text-3xl md:text-4xl text-primary-400"></i>
                      </div>
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-4">关于成全影院</h2>
                      <div className="space-y-4 text-sm text-foreground-300 leading-relaxed">
                        <p>
                          <strong className="text-foreground-100">成全影院</strong>成立于2020年，是国内领先的免费在线影视平台。我们致力于为广大影迷提供<strong className="text-foreground-100">高清流畅</strong>的观影体验，汇聚海量<strong className="text-foreground-100">电影大全</strong>、<strong className="text-foreground-100">电视剧全集</strong>、<strong className="text-foreground-100">综艺大全</strong>以及热门<strong className="text-foreground-100">动漫</strong>资源。
                        </p>
                        <p>
                          平台自上线以来，始终坚持<strong className="text-foreground-100">免费在线观看</strong>的理念，无需注册、无需付费，所有视频即点即看。我们采用先进的视频压缩与CDN分发技术，确保用户在任何网络环境下都能享受到<strong className="text-foreground-100">不卡顿</strong>的流畅观影体验。
                        </p>
                        <p>
                          成全影院内容团队每日精选<strong className="text-foreground-100">最新影片</strong>，覆盖动作、喜剧、爱情、科幻、恐怖、战争、动画等多种类型，满足不同观众的观影需求。无论是热映大片还是经典老片，在成全影院都能找到。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { icon: 'ri-vidicon-line', label: '影视资源', value: '10万+', desc: '海量影片库' },
                    { icon: 'ri-user-heart-line', label: '累计用户', value: '5000万+', desc: '忠实影迷选择' },
                    { icon: 'ri-timer-flash-line', label: '每日更新', value: '200+', desc: '最新内容上线' },
                    { icon: 'ri-shield-check-line', label: '安全稳定', value: '99.9%', desc: '服务可用率' },
                  ].map((stat, index) => (
                    <div key={index} className="bg-background-900/50 rounded-xl border border-background-800/50 p-5 text-center">
                      <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center">
                        <i className={`${stat.icon} text-2xl text-accent-400`}></i>
                      </div>
                      <p className="text-2xl font-bold text-foreground-50 mb-1">{stat.value}</p>
                      <p className="text-xs text-foreground-400">{stat.label}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{stat.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-background-900/50 rounded-2xl border border-background-800/50 p-6 md:p-10">
                  <h2 className="text-xl font-heading font-bold text-foreground-50 mb-4">核心优势</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { title: '免费在线观看', desc: '所有影片免费播放，无任何隐藏费用，无需订阅会员，打开即可观看。' },
                      { title: '高清不卡顿', desc: '采用H.265编码和全球CDN加速，流畅播放高清视频内容。' },
                      { title: '海量内容库', desc: '涵盖电影、电视剧、综艺、动漫四大品类，总量超过10万部。' },
                      { title: '每日更新', desc: '专业编辑团队每日精选最新影视资源，热门内容即时上线。' },
                      { title: '多端适配', desc: '完美支持PC、平板、手机等多终端，随时随地畅享观影。' },
                      { title: '智能推荐', desc: 'AI推荐引擎根据您的观影偏好，精准推送感兴趣的内容。' },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex-shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-primary-400"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-100 mb-1">{item.title}</h4>
                          <p className="text-xs text-foreground-400 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Reputation */}
            {activeTab === 'reputation' && (
              <section aria-label="用户口碑" className="space-y-10">
                <div className="bg-background-900/50 rounded-2xl border border-background-800/50 p-6 md:p-10 text-center">
                  <h2 className="text-xl font-heading font-bold text-foreground-50 mb-3">用户口碑</h2>
                  <p className="text-sm text-foreground-400 mb-8 max-w-xl mx-auto">
                    成全影院自上线以来，获得了广大影迷朋友的信赖与好评
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                      { label: '总体评分', value: '9.6', unit: '/10' },
                      { label: '用户好评率', value: '98.5', unit: '%' },
                      { label: '日均活跃用户', value: '200', unit: '万+' },
                      { label: '累计播放量', value: '50', unit: '亿+' },
                    ].map((item, i) => (
                      <div key={i} className="text-center">
                        <p className="text-3xl font-bold text-accent-400 mb-1">
                          {item.value}<span className="text-base text-foreground-400 ml-0.5">{item.unit}</span>
                        </p>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: '影迷小王', role: '忠实用户 · 3年', comment: '成全影院太好用了！资源特别全，想看什么都能找到。画质高清，播放也不卡，比很多付费平台还好用。真心推荐！', rating: 5 },
                    { name: '剧荒少女', role: '追剧达人 · 2年', comment: '自从发现成全影院，追剧再也不用愁了。热门电视剧同步更新，综艺也特别全。界面简洁，没有乱七八糟的广告，体验很棒！', rating: 5 },
                    { name: '动漫控阿杰', role: '动漫爱好者 · 1年', comment: '国产动漫资源特别全，更新也很快。斗罗大陆、完美世界这些热门国漫第一时间就能看到，画质也高清，太赞了！', rating: 5 },
                    { name: '老张看电影', role: '资深影迷 · 4年', comment: '用了四年了，资源越来越丰富。老片新片都有，分类也很详细。不收费这一点真的良心，希望一直做下去。', rating: 5 },
                    { name: '周末追剧人', role: '普通用户 · 2年', comment: '周末就喜欢躺在沙发上刷成全影院，电影电视剧综艺随便看。手机端体验也不错，地铁上也能追剧。', rating: 4 },
                    { name: '全家观影', role: '家庭用户 · 3年', comment: '家里老人小孩都喜欢看，动画片、战争片、家庭剧应有尽有。不用下载APP，浏览器打开就能看，太方便了。', rating: 5 },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-900/50 rounded-xl border border-background-800/50 p-5">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <div key={j} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className={`text-xs ${j < review.rating ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-600'}`}></i>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-300 leading-relaxed mb-4">{review.comment}</p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 text-xs font-bold">
                          {review.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-200">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Contact */}
            {activeTab === 'contact' && (
              <section aria-label="联系方式" className="space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮箱', content: 'service@chengquan.tv', desc: '商务合作、版权问题、意见反馈' },
                    { icon: 'ri-wechat-line', title: '微信公众号', content: '成全影院', desc: '关注获取最新影视资讯和推荐' },
                    { icon: 'ri-question-answer-line', title: '在线反馈', content: '站内留言板', desc: '技术问题、内容建议在线提交' },
                  ].map((item, i) => (
                    <div key={i} className="bg-background-900/50 rounded-xl border border-background-800/50 p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-500/10 flex items-center justify-center">
                        <i className={`${item.icon} text-xl text-primary-400`}></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-100 mb-1">{item.title}</h3>
                      <p className="text-sm font-medium text-accent-400 mb-1">{item.content}</p>
                      <p className="text-xs text-foreground-500">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-background-900/50 rounded-2xl border border-background-800/50 p-6 md:p-10">
                  <h2 className="text-xl font-heading font-bold text-foreground-50 mb-6 text-center">发送消息</h2>
                  <form
                    className="max-w-lg mx-auto"
                    onSubmit={(e) => {
                      e.preventDefault();
                      alert('感谢您的留言，我们会尽快回复！');
                    }}
                  >
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm text-foreground-400 mb-1.5">姓名</label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm text-foreground-400 mb-1.5">邮箱</label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm text-foreground-400 mb-1.5">主题</label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-400 mb-1.5">消息内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                          placeholder="请输入您的消息内容（500字以内）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </div>
                  </form>
                </div>

                <div className="bg-background-900/50 rounded-2xl border border-background-800/50 p-6 md:p-10">
                  <h2 className="text-xl font-heading font-bold text-foreground-50 mb-4">常见联系方式</h2>
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm font-semibold text-foreground-100">版权申诉</h4>
                      <p className="text-sm text-foreground-400 leading-relaxed">
                        如您认为成全影院上的内容侵犯了您的合法权益，请发送邮件至
                        <span className="text-accent-400"> copyright@chengquan.tv </span>
                        ，提供相关证明材料。我们会在收到邮件后的3个工作日内处理并回复。
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm font-semibold text-foreground-100">商务合作</h4>
                      <p className="text-sm text-foreground-400 leading-relaxed">
                        广告投放、内容合作、品牌联动等商务事宜，请联系
                        <span className="text-accent-400"> biz@chengquan.tv </span>
                        ，我们的商务团队将在24小时内与您取得联系。
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm font-semibold text-foreground-100">工作时间</h4>
                      <p className="text-sm text-foreground-400">
                        周一至周五 9:00 - 18:00（法定节假日除外）
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}